import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { sortBy } from 'lodash';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import Spinner from 'components/ui/Spinner';
import Table from 'components/v2/Table';
import AddButton from 'components/v2/buttons/AddButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import Checkbox from 'components/v2/forms/Checkbox';
import CargomaticSelect from 'components/v2/select';
import CargomaticTooltip from 'components/v2/Tooltip';
import {
  getReferenceNumberRequirements,
  upsertReferenceNumberRequirements,
  getReferenceNumbersMasterList,
} from 'utils/UserServiceAPIUtils';
import { showToast } from 'stores/ToastStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';

const OPTIONS = [
  {
    value: 'BOOKING_TIME',
    localizationSlug: 'BOOKING_TIME',
  },
  {
    value: 'PRIOR_TO_PICKUP',
    localizationSlug: 'PRIOR_TO_PICKUP',
  },
  {
    value: 'PRIOR_TO_DELIVERY',
    localizationSlug: 'PRIOR_TO_DELIVERY',
  },
  {
    value: 'PRIOR_TO_INVOICING',
    localizationSlug: 'PRIOR_TO_INVOICING',
  },
];

const UPDATE_MODE = {
  ADD: 'add',
  EDIT: 'edit',
};

const TABLE_ACTIONS = {
  REMOVE: 'remove',
};

const MASTER_LIST_LIMIT = 100;

const NON_EDITABLE_MASTER_REFERENCES = ['master_bol', 'booking_number'];

export default function ReferenceNumbers({ id, className }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [userReferenceNumbers, setUserReferenceNumbers] = useState([]);
  const [selectedReferenceNumbers, setSelectedReferenceNumbers] = useState([]);
  const [selectedRefNumber, setSelectedRefNumber] = useState();
  const [selectedRequiredAt, setSelectedRequiredAt] = useState();
  const [addRefNumber, setAddRefNumber] = useState();
  const [addRequiredAt, setAddRequiredAt] = useState();
  const [refresh, setRefresh] = useState({});
  const [tableAction, setTableAction] = useState('');
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [referenceNumberMasterList, setReferenceNumberMasterList] = useState(
    [],
  );

  const toggleAllRef = useRef();
  const userCanEditSettings =
    SessionStore.hasFinanceAdminRole() ||
    SessionStore.hasShipperOnboardingRole();

  const user = SessionStore.getUser();

  async function fetchUserReferenceNumbers(id) {
    try {
      const userResponse = await getReferenceNumberRequirements(id);
      if (!userResponse) {
        throw new Error('No response from API');
      }

      const referenceNumbers = sortBy(
        userResponse?.data?.getReferenceNumberRequirementsQuery ?? [],
        (ref) => ref?.reference_number?.slug?.toLowerCase(),
      );

      setUserReferenceNumbers(
        referenceNumbers?.map((refNumber) => {
          return {
            ...refNumber,
            id: refNumber.reference_number.slug,
            reference_number: refNumber.reference_number.slug,
          };
        }),
      );
    } catch (err) {
      showToast(t('ERROR_FETCHING_DATA'), null, 'error');
      console.error('Error during fetchUserReferenceNumbers: ', err.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchReferenceNumbersMasterList() {
    try {
      setIsLoading(true);
      const response = await getReferenceNumbersMasterList(MASTER_LIST_LIMIT);
      const queryResult = response?.data?.getReferenceNumbersQuery;
      if (!queryResult) {
        throw new Error('No response from API');
      }
      const result = queryResult?.reference_numbers ?? [];
      const slugs = result
        .filter((res) => !NON_EDITABLE_MASTER_REFERENCES.includes(res?.slug))
        .map((res) => res?.slug);
      const capitalizedSlugs = slugs?.map((slug) => {
        return {
          name: slug,
          localizationSlug: slug?.toUpperCase(),
        };
      });
      setReferenceNumberMasterList(capitalizedSlugs);
    } catch (err) {
      showToast(t('ERROR_FETCHING_DATA'), null, 'error');
      console.error(
        'Error during fetchReferenceNumbersMasterList: ',
        err.message,
      );
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setIsLoading(true);
    fetchUserReferenceNumbers(id);
  }, [refresh]);

  useEffect(() => {
    fetchReferenceNumbersMasterList();
  }, []);

  function renderSpinner() {
    return <Spinner showMessage message={t('LOADING')} />;
  }

  function handleToggleAllCheck(checked) {
    if (checked) {
      const selected = userReferenceNumbers
        .filter((row) => !NON_EDITABLE_MASTER_REFERENCES.includes(row.id))
        .map((row) => row.id);
      setSelectedReferenceNumbers(selected);
    } else {
      setSelectedReferenceNumbers([]);
    }
    if (toggleAllRef?.current?.indeterminate) {
      toggleAllRef.current.indeterminate = false;
    }
  }

  function isIndeterminate(selected) {
    const selectedCount = selected.length;
    return selectedCount > 0 && userReferenceNumbers?.length !== selectedCount;
  }

  function handleToggleCheck(id) {
    return function(checked) {
      let updatedSelections;
      if (checked) {
        updatedSelections = [...selectedReferenceNumbers, id];
        setSelectedReferenceNumbers(updatedSelections);
      } else {
        updatedSelections = selectedReferenceNumbers.filter(
          (item) => item !== id,
        );
        setSelectedReferenceNumbers(updatedSelections);
      }

      if (toggleAllRef?.current?.indeterminate) {
        toggleAllRef.current.indeterminate = isIndeterminate(updatedSelections);
      }
    };
  }

  function handleAddReferenceNumber(value) {
    setAddRefNumber(value?.name);
  }

  function handleAddRequiredAt(value) {
    setAddRequiredAt(value?.value);
  }

  function handleSelectedRequiredAt(value) {
    setSelectedRequiredAt(value?.value);
  }

  function handleCancelEdit() {
    setIsEditing(false);
    setSelectedRefNumber();
    setSelectedRequiredAt();
  }

  function handleShowEditMode(row) {
    setIsEditing(true);
    setSelectedRefNumber(row.reference_number);
    setSelectedRequiredAt(row.required_at);
  }

  async function handleSave(modeOfUpdating) {
    try {
      const refNumberName =
        modeOfUpdating === UPDATE_MODE.ADD ? addRefNumber : selectedRefNumber;
      const requirement = {
        reference_number:
          modeOfUpdating === UPDATE_MODE.ADD ? addRefNumber : selectedRefNumber,
        required_at:
          modeOfUpdating === UPDATE_MODE.ADD
            ? addRequiredAt
            : selectedRequiredAt,
      };

      const filteredRequirements = userReferenceNumbers
        ?.filter((userRefNum) => refNumberName !== userRefNum.id)
        ?.map((filteredRefNum) => {
          return {
            reference_number: filteredRefNum.reference_number,
            required_at: filteredRefNum.required_at,
          };
        });

      const response = await upsertReferenceNumberRequirements(id, [
        ...filteredRequirements,
        requirement,
      ], user._id);

      if (!response) {
        throw new Error('No response from API');
      }

      if (response?.data?.upsertReferenceNumberRequirementsMutation?.success) {
        setRefresh(requirement);
      }
      showToast(t('EDIT_REFERENCE_NUMBER_SUCCESS'));
    } catch (err) {
      console.error(
        'Error during upsertReferenceNumberRequirements: ',
        err.message,
      );
      showToast(t('EDIT_REFERENCE_NUMBER_FAILURE'), null, 'error');
    }
  }

  function handleEditOnSave() {
    handleSave(UPDATE_MODE.EDIT);
    setIsEditing(false);
    setSelectedRefNumber();
    setSelectedRequiredAt();
  }

  function handleAddOnSave() {
    handleSave(UPDATE_MODE.ADD);
    setAddRefNumber();
    setAddRequiredAt();
  }

  function handleRemoveSingleReferenceNumber(row) {
    removeReferenceNumber([row.id]);
  }

  function handleRemoveBulkReferenceNumbers() {
    removeReferenceNumber(selectedReferenceNumbers);
    setShowConfirmDeleteModal(false);
  }

  function handleReferenceNumberTableSelect(e) {
    const action = e?.target?.value;
    if (action === TABLE_ACTIONS.REMOVE) {
      setTableAction(action);
      setShowConfirmDeleteModal(true);
    }
  }

  function onHideConfirmDeleteModal() {
    setShowConfirmDeleteModal(false);
    handleToggleAllCheck(false);
  }

  async function removeReferenceNumber(referenceNumbersToRemove) {
    try {
      const filteredRequirements = userReferenceNumbers
        ?.filter(
          (userRefNum) => !referenceNumbersToRemove.includes(userRefNum.id),
        )
        ?.map((filteredRefNum) => {
          return {
            reference_number: filteredRefNum.reference_number,
            required_at: filteredRefNum.required_at,
          };
        });

      const response = await upsertReferenceNumberRequirements(
        id,
        filteredRequirements,
        user._id,
      );

      if (!response) {
        throw new Error('No response from API');
      }

      if (response?.data?.upsertReferenceNumberRequirementsMutation?.success) {
        setSelectedRefNumber();
        setSelectedRequiredAt();
        setTableAction('');
        setRefresh({});
      }

      showToast(t('REMOVE_REFERENCE_NUMBERS_SUCCESS'));
    } catch (err) {
      console.error('Error during removeReferenceNumber: ', err.message);
      showToast(t('REMOVE_REFERENCE_NUMBERS_FAILURE'), null, 'error');
    }
  }

  function renderFooter() {
    return (
      <tr>
        <td>&nbsp;</td>
        <td colSpan="1">
          <CargomaticSelect
            caption={t('CHOOSE_REFERENCE_NUMBER')}
            items={referenceNumberMasterList}
            showCaption
            value={addRefNumber}
            valueKey="name"
            textKey="localizationSlug"
            onSelect={handleAddReferenceNumber}
          />
        </td>
        <td colSpan="1">
          <CargomaticSelect
            items={OPTIONS}
            caption={t('CHOOSE_REQUIRED_AT')}
            showCaption
            value={addRequiredAt}
            valueKey="value"
            textKey="localizationSlug"
            onSelect={handleAddRequiredAt}
          />
        </td>
        <td>
          <div className="flex justify-end">
            <AddButton
              onClick={handleAddOnSave}
              disabled={!userCanEditSettings || !addRefNumber}
              color="green"
            />
          </div>
        </td>
      </tr>
    );
  }

  const columns = [
    {
      field: 'id',
      label: '',
      renderHeader: () => (
        <Checkbox
          id="id-renderHeader"
          checked={
            selectedReferenceNumbers?.length &&
            selectedReferenceNumbers?.length ===
            userReferenceNumbers.filter(
              (ref) => !NON_EDITABLE_MASTER_REFERENCES.includes(ref.id),
            )?.length
          }
          ref={toggleAllRef}
          onChange={handleToggleAllCheck}
        />
      ),
      render: (value) => {
        return (
          <Checkbox
            id="id-render"
            disabled={NON_EDITABLE_MASTER_REFERENCES.includes(value)}
            checked={selectedReferenceNumbers.includes(value)}
            onChange={handleToggleCheck(value)}
          />
        );
      },
    },
    {
      field: 'reference_number',
      label: t('REFERENCE_NUMBER'),
      render: (value) => <div className="w-32">{t(value?.toUpperCase())}</div>,
    },
    {
      field: 'required_at',
      label: t('REQUIRED_AT'),
      render: (value, row, colIndex) => {
        if (isEditing && row.reference_number === selectedRefNumber) {
          return (
            <CargomaticSelect
              items={OPTIONS}
              value={selectedRequiredAt}
              valueKey="value"
              textKey="localizationSlug"
              onSelect={handleSelectedRequiredAt}
            />
          );
        }
        return <span>{t(value)}</span>;
      },
    },
    {
      field: 'action',
      label: '',
      render: (value, row, index) => {
        if (isEditing && row.reference_number === selectedRefNumber) {
          return (
            <div className="flex items-center space-x-2 justify-end">
              <ApproveButton onClick={handleEditOnSave} />
              <DeclineButton color="gray" onClick={handleCancelEdit} />
            </div>
          );
        }
        if (NON_EDITABLE_MASTER_REFERENCES.includes(row.id)) {
          return (
            <div className="flex justify-end">
              <Dropdown useIcon>
                <Dropdown.Button disabled>{t('EDIT')}</Dropdown.Button>
                <Dropdown.Button disabled>
                  {titleize(t('REMOVE'))}
                </Dropdown.Button>
              </Dropdown>
            </div>
          );
        }
        return (
          <div className="flex justify-end">
            <Dropdown useIcon>
              <Dropdown.Button
                onClick={() => handleShowEditMode(row)}
                disabled={!userCanEditSettings}
              >
                {t('EDIT')}
              </Dropdown.Button>
              <Dropdown.Button
                onClick={() => handleRemoveSingleReferenceNumber(row)}
                disabled={!userCanEditSettings}
              >
                {titleize(t('REMOVE'))}
              </Dropdown.Button>
            </Dropdown>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <header className="flex items-center space-x-1">
          <h3 className="">{t('REQUIRED_REFERENCE_NUMBERS')}</h3>
          <CargomaticTooltip id="required-reference-numbers">
            {t('REQUIRED_REFERENCE_NUMBERS_TOOLTIP')}
          </CargomaticTooltip>
        </header>
        <select
          value={tableAction}
          onChange={handleReferenceNumberTableSelect}
          className="form-select"
        >
          <option value="">{t('SELECT_ACTION')}</option>
          <option
            value="remove"
            disabled={!userCanEditSettings || !selectedReferenceNumbers.length}
          >
            {titleize(t('REMOVE'))}
          </option>
        </select>
      </div>
      {isLoading ? (
        renderSpinner()
      ) : (
        <Table
          columns={columns}
          footer={renderFooter}
          data={userReferenceNumbers}
          headers={columns}
          className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
        />
      )}
      {showConfirmDeleteModal && (
        <ConfirmModal
          show={showConfirmDeleteModal}
          title={t('REMOVE_REFERENCE_NUMBERS')}
          message={t('REMOVE_REFERENCE_NUMBERS_MESSAGE')}
          onHide={onHideConfirmDeleteModal}
          onConfirm={handleRemoveBulkReferenceNumbers}
        />
      )}
    </>
  );
}
